import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Tags,
  Building2,
  MapPin,
  Users as UsersIcon,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const StatCard = ({ icon: Icon, label, value, to, gradient }) => (
  <Link
    to={to}
    className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-white/20 hover:bg-white/[0.04] transition-all overflow-hidden"
  >
    <div aria-hidden className="absolute inset-0 opacity-40 group-hover:opacity-70 transition-opacity" style={{ background: gradient }} />
    <div className="relative flex items-start justify-between">
      <div>
        <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/50">{label}</div>
        <div className="mt-3 text-3xl font-bold tabular-nums text-white">{value}</div>
      </div>
      <span className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
        <Icon className="w-4 h-4 text-white/70" />
      </span>
    </div>
    <div className="relative mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold text-white/70 group-hover:gap-2.5 transition-all">
      Manage <ArrowRight className="w-3.5 h-3.5" />
    </div>
  </Link>
);

const AdminOverview = () => {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState({ users: 0, regions: 0, customers: 0, priceLists: 0 });

  useEffect(() => {
    const load = async () => {
      try {
        const [customers, regions, priceLists] = await Promise.all([
          api.get('/customers'),
          api.get('/regions'),
          api.get('/price-lists'),
        ]);
        let usersCount = 0;
        if (isAdmin) {
          try {
            const u = await api.get('/users');
            usersCount = u.data.length;
          } catch (e) { /* ignore */ }
        }
        setStats({
          users: usersCount,
          regions: regions.data.length,
          customers: customers.data.length,
          priceLists: priceLists.data.length,
        });
      } catch (e) { /* ignore */ }
    };
    load();
  }, [isAdmin]);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-6xl">
      <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] font-semibold uppercase text-emerald-300">
        <TrendingUp className="w-3.5 h-3.5" /> Admin overview
      </div>
      <h1 className="mt-2 text-3xl md:text-4xl font-semibold text-white tracking-tight">
        Welcome back, {user?.full_name?.split(' ')[0] || 'Admin'}
      </h1>
      <p className="mt-2 text-white/55">
        Manage price lists, customers, regions and users. Region &amp; customer-specific overrides apply automatically in the PTI and Core estimators.
      </p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Tags}
          label="Price lists"
          value={stats.priceLists}
          to="/admin/price-lists"
          gradient="radial-gradient(400px 200px at 20% 0%, rgba(34,211,238,0.15), transparent 60%)"
        />
        <StatCard
          icon={Building2}
          label="Customers"
          value={stats.customers}
          to="/admin/customers"
          gradient="radial-gradient(400px 200px at 20% 0%, rgba(168,85,247,0.15), transparent 60%)"
        />
        <StatCard
          icon={MapPin}
          label="Regions"
          value={stats.regions}
          to="/admin/regions"
          gradient="radial-gradient(400px 200px at 20% 0%, rgba(52,211,153,0.12), transparent 60%)"
        />
        {isAdmin && (
          <StatCard
            icon={UsersIcon}
            label="Users"
            value={stats.users}
            to="/admin/users"
            gradient="radial-gradient(400px 200px at 20% 0%, rgba(250,204,21,0.10), transparent 60%)"
          />
        )}
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/pti" className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-white/20 hover:bg-white/[0.04] transition-all">
          <div className="text-white font-semibold">Open PTI Estimator →</div>
          <div className="mt-1 text-[13px] text-white/55">Public prompt-to-insights calculator with live MRR/ARR.</div>
        </Link>
        <Link to="/core" className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-white/20 hover:bg-white/[0.04] transition-all">
          <div className="text-white font-semibold">Open Core Estimator →</div>
          <div className="mt-1 text-[13px] text-white/55">Perpetual license configurator with region &amp; customer-specific price lists.</div>
        </Link>
      </div>
    </div>
  );
};

export default AdminOverview;
